import React, { useMemo } from 'react';
import './CheckboxGroup.scss';

import { Checkbox } from 'primereact/checkbox';
import { classNames } from 'primereact/utils';

type CheckboxOption = {
  label: string;
  value: string;
  disabled?: boolean;
};

interface Props {
  options: CheckboxOption[];
  value: string[];
  onChange: (selectedValues: string[]) => void;
  className?: string;
  orientation?: 'vertical' | 'horizontal';
  disabled: boolean;
  showSelectAll: boolean;
}

export const CheckboxGroup = ({
  options,
  value,
  onChange,
  className,
  orientation = 'vertical',
  disabled,
  showSelectAll,
}: Props) => {
  const availableOptions = useMemo(() => options.filter((option) => !option.disabled), [options]);
  const availableValues = useMemo(() => availableOptions.map((option) => option.value), [availableOptions]);
  const isAllSelected = useMemo(
    () => availableValues.length > 0 && availableValues.every((optionValue) => value.includes(optionValue)),
    [availableValues, value],
  );

  const handleCheckboxChange = (optionValue: string, checked: boolean) => {
    if (checked) {
      // Añadir el valor si está marcado
      const newValues = [...value, optionValue];
      onChange(newValues);
    } else {
      // Quitar el valor si no está marcado
      const newValues = value.filter((v) => v !== optionValue);
      onChange(newValues);
    }
  };

  const handleSelectAllChange = (checked: boolean) => {
    if (checked) {
      // Seleccionar todos los disponibles
      const newValues = [...new Set([...value, ...availableValues])];
      onChange(newValues);
    } else {
      // Deseleccionar todos los disponibles
      const newValues = value.filter((v) => !availableValues.includes(v));
      onChange(newValues);
    }
  };

  return (
    <div
      className={classNames('checkbox-group', `checkbox-group-${orientation}`, className, {
        'checkbox-group-disabled': disabled,
      })}
    >
      {showSelectAll && availableOptions.length > 0 && (
        <div className='checkbox-group-item checkbox-group-select-all'>
          <Checkbox checked={isAllSelected} onChange={(e) => handleSelectAllChange(!!e.checked)} disabled={disabled} />
          <label
            className={classNames('checkbox-label checkbox-select-all-label', {
              disabled: disabled,
            })}
            onClick={() => !disabled && handleSelectAllChange(!isAllSelected)}
          >
            Seleccionar todos
          </label>
        </div>
      )}

      {options.map((option) => {
        const isChecked = value.includes(option.value);
        const isDisabled = disabled || option.disabled;

        return (
          <div key={option.value} className='checkbox-group-item'>
            <Checkbox
              checked={isChecked}
              onChange={(e) => handleCheckboxChange(option.value, !!e.checked)}
              disabled={isDisabled}
            />
            <label
              className={classNames('checkbox-label', {
                disabled: isDisabled,
              })}
              onClick={() => !isDisabled && handleCheckboxChange(option.value, !isChecked)}
            >
              {option.label}
            </label>
          </div>
        );
      })}
    </div>
  );
};
